import { getProductMainPicture, placeholderImage } from '@/services/product'
import { TrashIcon } from '@/components/trash-icon'
import { GreenCheckmark } from '@/components/checkmark'
import { CookingTime, LinkWithoutPrefetch } from '@/components/product'
import { ExcludedElements } from '@/services/excluded-elements'
import { OrderedProductWithDetails } from '@/services/order'
import { BasketOrderPrices } from '@/services/basket'
import { Spinner } from './spinner'
import { useVenueContext } from '@/services/venue'
import { usePrice } from './price'
import { truly } from 'shared/utils/types'
import {
  BASKET_PRODUCT_ID_QUERY_PARAM,
  CAME_FROM_QUERY_PARAM,
} from '@/pages/venue/[venueName]/p/[productGroupName]/[productName]'
import { sum } from 'shared/utils/array'
import { useAppContext } from '@/pages/_app'
import { isProductExcludedFromOrderPriceModification } from 'shared/product'
import { useRouter } from 'next/router'

export type OrderedProductsProps = {
  onProductDelete?: (product: OrderedProductWithDetails) => void
  hasPreferences: boolean
  excludedElements: ExcludedElements
  isDeleteMode: boolean
  orderedProducts: OrderedProductWithDetails[]
  orderPrices: BasketOrderPrices
  style: 'simple' | 'detailed'
  attachProductLinks?: boolean
}

export function OrderedProducts(props: OrderedProductsProps) {
  const { t } = useAppContext()
  const venueContext = useVenueContext()!
  const { venueName } = venueContext
  const price = usePrice()
  const router = useRouter()

  const { isDeleteMode, orderedProducts, hasPreferences, excludedElements } = props

  return (
    <>
      <div className="mt-4 mb-4">
        {orderedProducts.map((orderedProduct, idx) => {
          const product = orderedProduct.product
          const isLastItem = idx === orderedProducts.length - 1
          const mainImage = getProductMainPicture(product)

          const titleWords = product.title.split(' ')
          const titleFirstWords = titleWords.slice(0, titleWords.length - 1)
          const titleLastWord = titleWords.slice(titleWords.length - 1, titleWords.length)

          const notFittingElements = orderedProduct.fitDescription
          const notFittingIngredientNames = notFittingElements.ingredients.map((i) => {
            return i.name
          })
          const notFittingRootComponentNames = notFittingElements.rootComponents.map((c) => {
            return c.name
          })

          const productIsExcludedFromPriceModifier =
            props.orderPrices !== 'loading' &&
            props.orderPrices.modifications &&
            props.orderPrices.modifications.length > 0 &&
            props.orderPrices.modifications.some((modification) => {
              const excludedProducts = modification.excludedProducts ?? []
              return (
                modification.priceAlternation < 0 &&
                isProductExcludedFromOrderPriceModification(product, excludedProducts)
              )
            })

          const key = [
            product.productGroupName,
            product.name,
            JSON.stringify(orderedProduct.appliedModifiers),
          ].join(' ')

          const modifierGroups = product.modifierGroups || [] // because old orders didn't have modifierGroups

          const modifiersPrice = sum(
            modifierGroups.map((modifierGroup) => {
              const appliedOptions = orderedProduct.appliedModifiers[modifierGroup.name]
              if (!appliedOptions) {
                return 0
              }

              return sum(
                modifierGroup.options.map((option) => {
                  if (!appliedOptions[option.name]) {
                    return 0
                  }

                  return option.price * orderedProduct.quantity
                }),
              )
            }),
          )

          const content = (
            <div className={`px-4 ${props.attachProductLinks ? 'cursor-pointer' : ''}`} key={key}>
              <div className="flex">
                <div className="relative w-24 flex-none mr-3">
                  <img
                    className={`mx-auto rounded-lg ${
                      mainImage ? '' : 'border border-opaqueLightest'
                    }`}
                    src={mainImage?.url || placeholderImage.url}
                    alt={product.title}
                    style={{ maxHeight: '6rem' }}
                  />
                  {isDeleteMode ? (
                    <div
                      onClick={(e) => {
                        e.preventDefault()
                        props.onProductDelete && props.onProductDelete(orderedProduct)
                      }}
                      className="absolute h-full w-full flex justify-center items-center rounded-lg"
                      style={{ top: 0, left: 0, backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
                    >
                      <div className="h-10 w-10 rounded-full bg-back dark:bg-backDark flex justify-center items-center">
                        <TrashIcon color="stroke-option" />
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="w-full">
                  <div className="flex-1 flex justify-between text-lg leading-tight mb-px">
                    <h3>
                      {props.style === 'detailed' ? (
                        <>
                          <span className="text-action font-bold">
                            {product.isUsingVolumeUnitsForQuantity && product.volume[0]
                              ? `${orderedProduct.quantity * product.volume[0]}${
                                  product.volumeUnit || ''
                                }`
                              : `${orderedProduct.quantity}x`}
                          </span>{' '}
                        </>
                      ) : null}
                      {titleFirstWords.join(' ')}{' '}
                      <span className="whitespace-nowrap">
                        {titleLastWord}{' '}
                        {notFittingElements.productFitsPreferences && hasPreferences ? (
                          <GreenCheckmark
                            className="relative inline w-4 h-4"
                            style={{ top: '-5px' }}
                          />
                        ) : null}
                      </span>
                    </h3>
                    {product.price * orderedProduct.quantity + modifiersPrice > 0 && (
                      <span className="ml-2 flex-shrink-0">
                        {price.format(product.price * orderedProduct.quantity + modifiersPrice)}
                      </span>
                    )}{' '}
                  </div>
                  {productIsExcludedFromPriceModifier && (
                    <div className="text-negative dark:text-negativeDark flex items-center">
                      {t('orderedProducts:noDiscount')}
                    </div>
                  )}
                  <div className="text-opaque dark:text-opaqueDark flex items-center">
                    {!product.isUsingVolumeUnitsForQuantity && (
                      <span className="mr-2">
                        {product.volume[0]} {product.volumeUnit}
                      </span>
                    )}
                    <CookingTime cookingTimeMin={product.cookingTimeMin} />
                  </div>
                  <div className="mt-1 text-opaque dark:text-opaqueDark leading-tight whitespace-pre-line">
                    {modifierGroups
                      .map((modifierGroup) => {
                        const appliedOptions = orderedProduct.appliedModifiers[modifierGroup.name]
                        if (!appliedOptions) {
                          return null
                        }

                        return `${modifierGroup.title}:\n${modifierGroup.options
                          .map((option) => {
                            if (!appliedOptions[option.name]) {
                              return null
                            }

                            return `• ${option.title}`
                          })
                          .filter(truly)
                          .join('\n')}`
                      })
                      .filter(truly)
                      .join('\n\n')}
                  </div>
                </div>
              </div>
              {(() => {
                const excludedIngredientNames: string[] = product.ingredients
                  .filter((ingredient) => {
                    return ingredient.isRemovable
                  })
                  .filter((ingredient) => {
                    const isFitting = !notFittingIngredientNames.includes(ingredient.name)
                    const excludedIngredientKey = {
                      productGroupName: product.productGroupName,
                      productName: product.name,
                      elementName: ingredient.name,
                    }
                    const isExcludedByUser = excludedElements.ingredients.get(excludedIngredientKey)
                    const excluded =
                      isExcludedByUser !== undefined ? !!isExcludedByUser : !isFitting

                    return excluded
                  })
                  .map((ingredient) => {
                    return ingredient.title
                  })

                const rootComponentsNames: string[] = product.components
                  .filter((rootComponents) => {
                    return rootComponents.isRemovable
                  })
                  .filter((rootComponent) => {
                    const isFitting = !notFittingRootComponentNames.includes(rootComponent.name)
                    const excludedRootComponentKey = {
                      productGroupName: product.productGroupName,
                      productName: product.name,
                      elementName: rootComponent.name,
                    }
                    const isExcludedByUser =
                      excludedElements.rootComponents.get(excludedRootComponentKey)
                    const excluded = isExcludedByUser !== undefined ? isExcludedByUser : !isFitting

                    return excluded
                  })
                  .map((rootComponent) => {
                    return rootComponent.title
                  })

                const excludedElementTitles = [...excludedIngredientNames, ...rootComponentsNames]
                if (excludedElementTitles.length === 0) {
                  return null
                }
                return (
                  <div className="mt-4 pl-2 border-l-8 border-backOpaque dark:border-backOpaqueDark leading-tight text-negative dark:text-negativeDark">
                    <div>{t('orderedProducts:withoutIngredients')}</div>
                    {excludedElementTitles.map((title) => {
                      return <div key={title}>— {title}</div>
                    })}
                  </div>
                )
              })()}
              {!isLastItem ? (
                <div className="my-4 border-b border-ghost dark:border-ghostDark">
                  {/* horizontal separator */}
                </div>
              ) : null}
            </div>
          )

          if (!props.attachProductLinks) {
            return content
          }

          const queryParams = new URLSearchParams({
            ...(router.route === '/venue/[venueName]/checkout'
              ? { [CAME_FROM_QUERY_PARAM]: 'checkout' }
              : {}),
            ...(orderedProduct.basketProductId
              ? { [BASKET_PRODUCT_ID_QUERY_PARAM]: orderedProduct.basketProductId }
              : {}),
          }).toString()

          return (
            <LinkWithoutPrefetch
              key={key}
              href={`/venue/${venueName}/p/${product.productGroupName}/${product.name}${
                queryParams ? `?${queryParams}` : ''
              }`}
            >
              {content}
            </LinkWithoutPrefetch>
          )
        })}
      </div>
      {props.style === 'detailed' ? (
        <div className="pt-2">
          {props.orderPrices !== 'loading' && props.orderPrices.modifications
            ? props.orderPrices.modifications
                .filter((modification) => {
                  return modification.priceAlternation !== 0
                })
                .map((modification) => {
                  return (
                    <div key={modification.caption}>
                      <div className="px-4">
                        <div className="mt-2 mb-2 border-b border-ghost dark:border-ghostDark">
                          {/* horizontal separator */}
                        </div>
                      </div>
                      <div className="min-h-8 flex items-center justify-between px-4 text-lg">
                        <span>
                          {(() => {
                            const { percentage } = modification
                            const percentageCaption = Math.floor(percentage * 100)

                            return (
                              <>
                                {percentageCaption > 0 && (
                                  <span className="font-bold text-action">
                                    {percentageCaption}%{' '}
                                  </span>
                                )}
                                <span>{modification.caption}</span>
                              </>
                            )
                          })()}
                        </span>
                        <span
                          className={`${modification.priceAlternation < 0 ? 'text-action' : ''}`}
                        >
                          {price.format(modification.priceAlternation)}
                        </span>
                      </div>
                    </div>
                  )
                })
            : null}
          <div className="px-4">
            <div className="mt-2 mb-2 border-b border-ghost dark:border-ghostDark">
              {/* horizontal separator */}
            </div>
          </div>
          <div className="h-8 flex justify-between px-4 text-xl font-medium">
            <span>{t('common:total')}</span>
            <span>
              {props.orderPrices === 'loading' ? (
                <div className="ml-auto w-8">
                  <Spinner />
                </div>
              ) : (
                price.format(props.orderPrices.total)
              )}
            </span>
          </div>
        </div>
      ) : null}
      <div className="h-4"></div>
    </>
  )
}
